import { Module } from '../../types';

export const dspyModule9: Module = {
  id: 'dspy-module9',
  title: 'Best Practices and Patterns',
  description:
    'Learn proven strategies for building robust, maintainable, and high-performing DSPy applications while avoiding common pitfalls.',
  duration: '3-4 hours',
  isUnderDevelopment: true,
  content: `# Module 9: Best Practices and Patterns

## Introduction

In this final module, we'll consolidate the best practices and design patterns that lead to successful DSPy applications. You'll learn proven strategies for building robust, maintainable, and high-performing language model programs while avoiding common pitfalls.

## Learning Objectives

By the end of this module, you will be able to:
- Apply architectural best practices for DSPy applications
- Implement effective design patterns for common use cases
- Avoid common pitfalls and debugging challenges
- Build maintainable and scalable DSPy systems
- Optimize for both development velocity and runtime performance

## 1. Architectural Best Practices

### 1.1 Program Design Principles

**Modular Architecture**
\`\`\`python
# Good: Modular, reusable components
class DocumentProcessor(dspy.Module):
    def __init__(self):
        super().__init__()
        self.extract = ExtractInfo()
        self.summarize = Summarize()
        self.analyze = AnalyzeContent()
    
    def forward(self, document):
        # Each module has a single responsibility
        info = self.extract(document=document)
        summary = self.summarize(text=info.content)
        analysis = self.analyze(
            content=info.content,
            summary=summary.text
        )
        return analysis

# Bad: Monolithic design
class ProcessEverything(dspy.Module):
    def forward(self, document):
        # Too many responsibilities in one module
        # Hard to test, optimize, and maintain
        ...
\`\`\`

**Signature Design**
\`\`\`python
# Good: Clear, focused signatures
class ExtractKeyPoints(dspy.Signature):
    """Extract the main points from a document."""
    document: str = dspy.InputField(
        desc="The document to analyze"
    )
    key_points: list[str] = dspy.OutputField(
        desc="List of 3-5 main points"
    )

# Bad: Overly complex signature
class DoEverything(dspy.Signature):
    """Process document completely."""
    document: str = dspy.InputField()
    summary: str = dspy.OutputField()
    key_points: list[str] = dspy.OutputField()
    sentiment: str = dspy.OutputField()
    categories: list[str] = dspy.OutputField()
    # Too many outputs for one operation
\`\`\`

### 1.2 Error Handling and Reliability

**Robust Error Handling**
\`\`\`python
class RobustQA(dspy.Module):
    def __init__(self):
        super().__init__()
        self.generate_answer = dspy.ChainOfThought(AnswerQuestion)
    
    def forward(self, question, context):
        try:
            # Primary attempt
            answer = self.generate_answer(
                question=question,
                context=context
            )
            
            # Validate answer
            if not answer.answer or len(answer.answer.strip()) == 0:
                raise ValueError("Empty answer generated")
            
            return answer
            
        except Exception as e:
            # Fallback strategy
            logger.warning(f"Primary attempt failed: {e}")
            
            # Try with simplified prompt
            simple_answer = dspy.Predict("question, context -> answer")
            return simple_answer(question=question, context=context)
\`\`\`

**Assertion-Based Validation**
\`\`\`python
class ValidatedGeneration(dspy.Module):
    def __init__(self):
        super().__init__()
        self.generate = dspy.Predict(GenerateContent)
    
    def forward(self, topic, requirements):
        content = self.generate(
            topic=topic,
            requirements=requirements
        )
        
        # Enforce constraints
        dspy.Assert(
            len(content.text) >= 100,
            "Content must be at least 100 characters"
        )
        
        dspy.Assert(
            all(req in content.text.lower() 
                for req in requirements.lower().split(",")),
            "Content must address all requirements"
        )
        
        # Soft constraints
        dspy.Suggest(
            len(content.text) <= 500,
            "Content should be concise (under 500 characters)"
        )
        
        return content
\`\`\`

### 1.3 Performance Optimization

**Efficient Caching Strategy**
\`\`\`python
# Configure multi-layer caching
client = dspy.LM(
    model='openai/gpt-4',
    cache=True,  # Enable caching
    cache_dir="./cache/production"
)

# Use caching effectively
class EfficientProcessor(dspy.Module):
    def __init__(self):
        super().__init__()
        # Expensive operations that benefit from caching
        self.classify = dspy.Predict(ClassifyDocument)
        self.extract = dspy.Predict(ExtractEntities)
        
        # Lightweight operations that don't need caching
        self.format = FormatOutput()  # Simple formatting
    
    def forward(self, documents):
        results = []
        
        # Process in batches for better cache utilization
        for batch in self.batch_documents(documents, size=10):
            classifications = dspy.Parallel(
                lambda doc: self.classify(document=doc),
                batch
            )
            
            # Only extract from relevant documents
            relevant_docs = [
                doc for doc, cls in zip(batch, classifications)
                if cls.category in ['important', 'critical']
            ]
            
            if relevant_docs:
                entities = dspy.Parallel(
                    lambda doc: self.extract(document=doc),
                    relevant_docs
                )
                results.extend(entities)
        
        return results
\`\`\`

**Parallel Processing**
\`\`\`python
class ParallelAnalyzer(dspy.Module):
    def __init__(self):
        super().__init__()
        self.analyze = dspy.ChainOfThought(AnalyzeText)
    
    def forward(self, texts):
        # Good: Process multiple texts in parallel
        results = dspy.Parallel(
            lambda text: self.analyze(text=text),
            texts,
            max_workers=5  # Control concurrency
        )
        
        # Aggregate results efficiently
        summary = self.aggregate_results(results)
        return summary
    
    def aggregate_results(self, results):
        # Efficient aggregation logic
        themes = defaultdict(int)
        sentiments = []
        
        for result in results:
            for theme in result.themes:
                themes[theme] += 1
            sentiments.append(result.sentiment)
        
        return {
            'top_themes': sorted(themes.items(), 
                               key=lambda x: x[1], 
                               reverse=True)[:5],
            'avg_sentiment': sum(sentiments) / len(sentiments)
        }
\`\`\`

## 2. Common Design Patterns

### 2.1 Chain of Responsibility Pattern

\`\`\`python
class ChainedProcessor(dspy.Module):
    """Process documents through a chain of handlers."""
    
    def __init__(self):
        super().__init__()
        self.handlers = [
            PreprocessHandler(),
            ValidationHandler(),
            EnrichmentHandler(),
            OutputHandler()
        ]
    
    def forward(self, document):
        result = document
        
        for handler in self.handlers:
            result = handler.process(result)
            
            # Early exit if handler indicates completion
            if hasattr(result, 'completed') and result.completed:
                break
        
        return result

class ValidationHandler(dspy.Module):
    def __init__(self):
        super().__init__()
        self.validate = dspy.Predict(ValidateDocument)
    
    def process(self, document):
        validation = self.validate(document=document)
        
        if not validation.is_valid:
            # Return error result
            return ErrorResult(
                document=document,
                error=validation.error_message,
                completed=True
            )
        
        # Pass to next handler
        return document
\`\`\`

### 2.2 Strategy Pattern

\`\`\`python
class AdaptiveQA(dspy.Module):
    """Adaptive QA that selects strategy based on question type."""
    
    def __init__(self):
        super().__init__()
        self.classifier = dspy.Predict(ClassifyQuestion)
        
        # Different strategies for different question types
        self.strategies = {
            'factual': FactualQAStrategy(),
            'analytical': AnalyticalQAStrategy(),
            'comparative': ComparativeQAStrategy(),
            'creative': CreativeQAStrategy()
        }
        
        # Default fallback
        self.default = dspy.ChainOfThought(AnswerQuestion)
    
    def forward(self, question, context):
        # Classify question type
        classification = self.classifier(question=question)
        
        # Select appropriate strategy
        strategy = self.strategies.get(
            classification.question_type,
            self.default
        )
        
        # Execute selected strategy
        return strategy(question=question, context=context)

class AnalyticalQAStrategy(dspy.Module):
    def __init__(self):
        super().__init__()
        self.decompose = dspy.Predict(DecomposeQuestion)
        self.analyze = dspy.ChainOfThought(AnalyzeAspect)
        self.synthesize = dspy.Predict(SynthesizeAnalysis)
    
    def forward(self, question, context):
        # Decompose complex question
        sub_questions = self.decompose(question=question)
        
        # Analyze each aspect
        analyses = []
        for sub_q in sub_questions.questions:
            analysis = self.analyze(
                question=sub_q,
                context=context
            )
            analyses.append(analysis)
        
        # Synthesize comprehensive answer
        return self.synthesize(
            original_question=question,
            analyses=analyses
        )
\`\`\`

### 2.3 Observer Pattern for Monitoring

\`\`\`python
class MonitoredPipeline(dspy.Module):
    """Pipeline with comprehensive monitoring."""
    
    def __init__(self):
        super().__init__()
        self.observers = []
        self.stages = {
            'preprocess': PreprocessStage(),
            'enrich': EnrichmentStage(),
            'generate': GenerationStage(),
            'validate': ValidationStage()
        }
    
    def attach_observer(self, observer):
        self.observers.append(observer)
    
    def notify_observers(self, event, data):
        for observer in self.observers:
            observer.update(event, data)
    
    def forward(self, input_data):
        result = input_data
        
        for stage_name, stage in self.stages.items():
            start_time = time.time()
            
            try:
                result = stage(result)
                
                # Notify success
                self.notify_observers('stage_complete', {
                    'stage': stage_name,
                    'duration': time.time() - start_time,
                    'success': True
                })
                
            except Exception as e:
                # Notify failure
                self.notify_observers('stage_failed', {
                    'stage': stage_name,
                    'error': str(e),
                    'duration': time.time() - start_time
                })
                raise
        
        return result

# Usage with observers
pipeline = MonitoredPipeline()
pipeline.attach_observer(MetricsCollector())
pipeline.attach_observer(LoggingObserver())
pipeline.attach_observer(AlertingObserver())
\`\`\`

## 3. Common Pitfalls and Solutions

### 3.1 Context Length Management

**Problem: Context Too Long Errors**
\`\`\`python
# Bad: Uncontrolled context growth
class NaiveRAG(dspy.Module):
    def forward(self, question, documents):
        # This can exceed context limits
        context = "\\n".join(documents)
        return self.answer(question=question, context=context)

# Good: Smart context management
class SmartRAG(dspy.Module):
    def __init__(self, max_context_length=3000):
        super().__init__()
        self.max_context = max_context_length
        self.answer = dspy.ChainOfThought(AnswerQuestion)
    
    def forward(self, question, documents):
        # Rank and select most relevant documents
        ranked_docs = self.rank_documents(question, documents)
        
        # Build context within limits
        context_parts = []
        current_length = 0
        
        for doc in ranked_docs:
            doc_length = len(doc)
            if current_length + doc_length > self.max_context:
                break
            context_parts.append(doc)
            current_length += doc_length
        
        context = "\\n".join(context_parts)
        return self.answer(question=question, context=context)
\`\`\`

### 3.2 Optimization Pitfalls

**Problem: Overfitting During Optimization**
\`\`\`python
# Bad: Overfitting to small dataset
teleprompter = dspy.BootstrapFewShot(
    metric=exact_match,
    max_bootstrapped_demos=20  # Too many for small dataset
)

# Good: Balanced optimization
class BalancedOptimizer:
    def __init__(self, trainset, valset):
        self.trainset = trainset
        self.valset = valset
    
    def optimize(self, program):
        # Use appropriate number of demos
        demo_count = min(
            len(self.trainset) // 10,  # 10% of training data
            8  # Maximum cap
        )
        
        # Configure teleprompter
        teleprompter = dspy.BootstrapFewShot(
            metric=self.balanced_metric,
            max_bootstrapped_demos=demo_count,
            max_labeled_demos=demo_count // 2
        )
        
        # Compile with validation
        compiled = teleprompter.compile(
            program,
            trainset=self.trainset[:100],  # Use subset
            valset=self.valset
        )
        
        return compiled
    
    def balanced_metric(self, example, prediction):
        # Multi-faceted evaluation
        accuracy = prediction.answer == example.answer
        length_ok = 10 <= len(prediction.answer) <= 200
        relevant = any(
            keyword in prediction.answer.lower()
            for keyword in example.keywords
        )
        
        return accuracy * 0.5 + length_ok * 0.25 + relevant * 0.25
\`\`\`

### 3.3 Type Safety and Validation

**Problem: Runtime Type Errors**
\`\`\`python
# Bad: No type validation
class UnsafeProcessor(dspy.Module):
    def forward(self, data):
        # Assumes data structure without validation
        return self.process(items=data['items'])

# Good: Type-safe implementation
from typing import List, Optional
from pydantic import BaseModel, validator

class ProcessedItem(BaseModel):
    id: str
    content: str
    score: float
    
    @validator('score')
    def score_must_be_valid(cls, v):
        if not 0 <= v <= 1:
            raise ValueError('Score must be between 0 and 1')
        return v

class TypeSafeProcessor(dspy.Module):
    def __init__(self):
        super().__init__()
        self.process = dspy.Predict(ProcessItems)
    
    def forward(self, items: List[dict]) -> List[ProcessedItem]:
        # Validate input
        validated_items = []
        for item in items:
            if not isinstance(item, dict):
                raise TypeError(f"Expected dict, got {type(item)}")
            if 'content' not in item:
                raise ValueError("Item missing required 'content' field")
            validated_items.append(item)
        
        # Process with type safety
        result = self.process(items=validated_items)
        
        # Validate output
        processed = []
        for item in result.processed_items:
            processed.append(ProcessedItem(**item))
        
        return processed
\`\`\`

## 4. Testing and Quality Assurance

### 4.1 Comprehensive Testing Strategy

\`\`\`python
class TestableModule(dspy.Module):
    """Example of a well-tested DSPy module."""
    
    def __init__(self):
        super().__init__()
        self.preprocess = dspy.Predict(PreprocessText)
        self.analyze = dspy.ChainOfThought(AnalyzeContent)
        self.format = dspy.Predict(FormatOutput)
    
    def forward(self, text, requirements=None):
        # Preprocessing with validation
        processed = self.preprocess(text=text)
        
        # Analysis with requirements
        analysis = self.analyze(
            content=processed.cleaned_text,
            requirements=requirements or "standard analysis"
        )
        
        # Format output
        return self.format(
            analysis=analysis.result,
            style="professional"
        )

# Unit tests
class TestTestableModule:
    def setup_method(self):
        """Setup for each test."""
        self.module = TestableModule()
        
    def test_basic_functionality(self):
        """Test basic operation."""
        result = self.module(
            text="Sample text for analysis.",
            requirements="Identify key themes"
        )
        
        assert result is not None
        assert hasattr(result, 'formatted_output')
        assert len(result.formatted_output) > 0
    
    def test_edge_cases(self):
        """Test edge cases."""
        # Empty input
        with pytest.raises(ValueError):
            self.module(text="")
        
        # Very long input
        long_text = "word " * 10000
        result = self.module(text=long_text)
        assert result is not None
        
        # Special characters
        special_text = "Test with émojis 🚀 and symbols #@!"
        result = self.module(text=special_text)
        assert result is not None
    
    def test_requirements_handling(self):
        """Test different requirement scenarios."""
        text = "Analyze this content"
        
        # No requirements (uses default)
        result1 = self.module(text=text)
        
        # Specific requirements
        result2 = self.module(
            text=text,
            requirements="Focus on sentiment and entities"
        )
        
        # Results should differ based on requirements
        assert result1.formatted_output != result2.formatted_output
\`\`\`

### 4.2 Integration Testing

\`\`\`python
class IntegrationTest:
    """Test complete pipeline integration."""
    
    def test_full_pipeline(self):
        """Test end-to-end pipeline."""
        # Setup pipeline
        pipeline = RAGPipeline()
        
        # Prepare test data
        documents = [
            "Python is a programming language.",
            "Machine learning uses Python extensively.",
            "DSPy simplifies LM programming."
        ]
        
        questions = [
            "What is Python?",
            "How is Python used in ML?",
            "What does DSPy do?"
        ]
        
        # Test pipeline
        for question in questions:
            result = pipeline(
                question=question,
                documents=documents
            )
            
            # Validate result structure
            assert hasattr(result, 'answer')
            assert hasattr(result, 'confidence')
            assert hasattr(result, 'sources')
            
            # Validate content
            assert len(result.answer) > 0
            assert 0 <= result.confidence <= 1
            assert len(result.sources) > 0
    
    def test_error_recovery(self):
        """Test pipeline error handling."""
        pipeline = RobustPipeline()
        
        # Test with problematic inputs
        problematic_inputs = [
            {"question": None, "documents": []},
            {"question": "", "documents": ["test"]},
            {"question": "test", "documents": None}
        ]
        
        for input_data in problematic_inputs:
            # Should handle gracefully
            result = pipeline(**input_data)
            assert result.status in ['error', 'fallback']
            assert result.error_message is not None
\`\`\`

## 5. Production Best Practices

### 5.1 Configuration Management

\`\`\`python
from dataclasses import dataclass
from typing import Optional
import os
import json

@dataclass
class DSPyConfig:
    """Centralized configuration for DSPy applications."""
    
    # Model configuration
    model: str = "openai/gpt-4"
    temperature: float = 0.0
    max_tokens: int = 1000
    
    # Caching configuration
    cache_enabled: bool = True
    cache_dir: str = "./cache"
    
    # Optimization settings
    max_bootstrapped_demos: int = 4
    max_labeled_demos: int = 4
    optimization_metric: str = "accuracy"
    
    # Runtime settings
    max_retries: int = 3
    timeout_seconds: int = 30
    parallel_workers: int = 5
    
    @classmethod
    def from_env(cls):
        """Load configuration from environment variables."""
        return cls(
            model=os.getenv("DSPY_MODEL", cls.model),
            temperature=float(os.getenv("DSPY_TEMPERATURE", cls.temperature)),
            max_tokens=int(os.getenv("DSPY_MAX_TOKENS", cls.max_tokens)),
            cache_enabled=os.getenv("DSPY_CACHE_ENABLED", "true").lower() == "true",
            cache_dir=os.getenv("DSPY_CACHE_DIR", cls.cache_dir),
            max_bootstrapped_demos=int(os.getenv("DSPY_MAX_DEMOS", cls.max_bootstrapped_demos)),
            parallel_workers=int(os.getenv("DSPY_WORKERS", cls.parallel_workers))
        )
    
    @classmethod
    def from_file(cls, filepath: str):
        """Load configuration from JSON file."""
        with open(filepath, 'r') as f:
            data = json.load(f)
        return cls(**data)

# Usage
config = DSPyConfig.from_env()
client = dspy.LM(
    model=config.model,
    temperature=config.temperature,
    max_tokens=config.max_tokens,
    cache=config.cache_enabled
)
\`\`\`

### 5.2 Monitoring and Observability

\`\`\`python
class ObservablePipeline(dspy.Module):
    """Pipeline with comprehensive observability."""
    
    def __init__(self, config: DSPyConfig):
        super().__init__()
        self.config = config
        self.metrics = MetricsCollector()
        
        # Initialize stages
        self.stages = self._initialize_stages()
    
    def forward(self, request):
        trace_id = str(uuid.uuid4())
        start_time = time.time()
        
        # Start trace
        with self.metrics.trace(trace_id) as trace:
            try:
                # Log request
                trace.log_event("request_received", {
                    "request_id": request.id,
                    "timestamp": start_time
                })
                
                # Process through stages
                result = request
                for stage_name, stage in self.stages.items():
                    stage_start = time.time()
                    
                    result = stage(result)
                    
                    # Record stage metrics
                    self.metrics.record_stage(
                        stage_name,
                        duration=time.time() - stage_start,
                        success=True
                    )
                    
                    trace.log_event(f"{stage_name}_complete", {
                        "duration": time.time() - stage_start
                    })
                
                # Record success
                self.metrics.record_request(
                    success=True,
                    duration=time.time() - start_time
                )
                
                return result
                
            except Exception as e:
                # Record failure
                self.metrics.record_request(
                    success=False,
                    duration=time.time() - start_time,
                    error=str(e)
                )
                
                trace.log_error(str(e))
                raise
\`\`\`

### 5.3 Deployment Patterns

\`\`\`python
class ProductionDeployment:
    """Production-ready deployment patterns."""
    
    def __init__(self, config: DSPyConfig):
        self.config = config
        self.pipeline = self._build_pipeline()
        self.health_checker = HealthChecker()
    
    def _build_pipeline(self):
        """Build optimized pipeline."""
        # Load compiled program
        program = DSPyProgram()
        program.load(self.config.compiled_program_path)
        
        # Wrap with production features
        return ProductionWrapper(
            program=program,
            timeout=self.config.timeout_seconds,
            retries=self.config.max_retries
        )
    
    async def process_request(self, request):
        """Process with full production features."""
        # Health check
        if not await self.health_checker.is_healthy():
            raise ServiceUnavailableError("Service unhealthy")
        
        # Rate limiting
        if not self.rate_limiter.allow(request.client_id):
            raise RateLimitError("Rate limit exceeded")
        
        # Process with timeout
        try:
            result = await asyncio.wait_for(
                self.pipeline.aprocess(request),
                timeout=self.config.timeout_seconds
            )
            
            # Log success
            logger.info(f"Processed request {request.id} successfully")
            return result
            
        except asyncio.TimeoutError:
            logger.error(f"Request {request.id} timed out")
            raise
        except Exception as e:
            logger.error(f"Request {request.id} failed: {e}")
            raise
    
    def export_for_deployment(self):
        """Export all artifacts for deployment."""
        artifacts = {
            'program': self.pipeline.export(),
            'config': self.config.__dict__,
            'version': self.get_version(),
            'dependencies': self.get_dependencies()
        }
        
        # Save artifacts
        with open('deployment_artifacts.json', 'w') as f:
            json.dump(artifacts, f, indent=2)
        
        logger.info("Exported deployment artifacts")
        return artifacts
\`\`\`

## Summary and Next Steps

### Key Takeaways

1. **Design Principles**
   - Build modular, composable programs
   - Use clear, focused signatures
   - Implement proper error handling
   - Optimize for performance and cost

2. **Common Patterns**
   - Chain of Responsibility for sequential processing
   - Strategy Pattern for adaptive behavior
   - Observer Pattern for monitoring
   - Factory Pattern for dynamic module creation

3. **Avoid Pitfalls**
   - Manage context length carefully
   - Prevent overfitting during optimization
   - Ensure type safety and validation
   - Handle edge cases gracefully

4. **Production Readiness**
   - Implement comprehensive testing
   - Use configuration management
   - Add monitoring and observability
   - Follow deployment best practices

### Your Journey Forward

1. **Practice Building**
   - Start with simple programs and gradually increase complexity
   - Experiment with different optimizers and configurations
   - Build real applications to gain practical experience

2. **Contribute to the Community**
   - Share your DSPy programs and patterns
   - Contribute to the DSPy repository
   - Help others learn through tutorials and examples

3. **Stay Updated**
   - Follow DSPy development and new features
   - Experiment with new models and capabilities
   - Join the DSPy community discussions

## Practical Exercise: Building a Complete Application

Build a production-ready customer support system that:

1. **Classifies** incoming support tickets
2. **Routes** to appropriate handlers
3. **Generates** initial responses
4. **Escalates** complex issues
5. **Monitors** performance

Requirements:
- Use multiple design patterns
- Implement comprehensive error handling
- Add monitoring and metrics
- Include configuration management
- Write tests for critical paths

\`\`\`python
# Starter template
class CustomerSupportSystem(dspy.Module):
    def __init__(self, config: DSPyConfig):
        super().__init__()
        # Initialize your components here
        
    def forward(self, ticket):
        # Implement your pipeline here
        pass

# Build and optimize your system
system = CustomerSupportSystem(config)
optimized = optimize_system(system, training_data)

# Deploy with monitoring
deployment = ProductionDeployment(optimized, config)
\`\`\`

This exercise brings together all concepts from the course, challenging you to build a complete, production-ready DSPy application.

## Additional Resources

- DSPy GitHub Repository: https://github.com/stanfordnlp/dspy
- DSPy Documentation: https://dspy-docs.vercel.app/
- Community Examples: https://github.com/stanfordnlp/dspy/tree/main/examples
- Research Papers: DSPy arXiv papers and publications

Congratulations on completing the DSPy course! You now have the knowledge and tools to build sophisticated language model applications using the DSPy framework.`,
  exercises: [
    {
      id: 'dspy-m9-ex1',
      title: 'Implement Error Handling Pattern',
      description: 'Create a robust DSPy module with comprehensive error handling',
      difficulty: 'intermediate',
      type: 'implementation',
      instructions:
        'Implement a DSPy module that processes user input with proper error handling, including validation, fallbacks, and error reporting.',
      hints: [
        'Use try-except blocks for error catching',
        'Implement validation with dspy.Assert',
        'Create fallback strategies for failures',
        'Include proper error messages and logging',
      ],
      solution: `class RobustProcessor(dspy.Module):
    def __init__(self):
        super().__init__()
        self.primary = dspy.ChainOfThought("input -> output")
        self.fallback = dspy.Predict("input -> output")
        self.validator = dspy.Predict("output -> is_valid, error_message")
    
    def forward(self, user_input):
        try:
            # Validate input
            if not user_input or len(user_input.strip()) == 0:
                raise ValueError("Empty input provided")
            
            # Primary processing
            result = self.primary(input=user_input)
            
            # Validate output
            validation = self.validator(output=result.output)
            if not validation.is_valid:
                raise ValueError(f"Invalid output: {validation.error_message}")
            
            return result
            
        except Exception as e:
            logger.warning(f"Primary processing failed: {e}")
            
            # Try fallback
            try:
                fallback_result = self.fallback(input=user_input)
                return fallback_result
            except Exception as fallback_error:
                logger.error(f"Fallback also failed: {fallback_error}")
                return dspy.Prediction(
                    output="Unable to process request",
                    error=str(e),
                    success=False
                )`,
      testCases: [
        {
          input: 'Valid input text',
          expected: 'Successful processing',
          description: 'Should process valid input successfully',
        },
        {
          input: '',
          expected: 'Error handling triggered',
          description: 'Should handle empty input gracefully',
        },
      ],
    },
    {
      id: 'dspy-m9-ex2',
      title: 'Optimize for Context Length',
      description: 'Implement a context-aware RAG system that manages context length',
      difficulty: 'advanced',
      type: 'optimization',
      instructions:
        'Create a RAG system that intelligently manages context length to avoid token limit errors while maintaining answer quality.',
      hints: [
        'Implement document ranking based on relevance',
        'Track token counts for context building',
        'Use chunking strategies for long documents',
        'Implement dynamic context adjustment',
      ],
      solution: `class ContextAwareRAG(dspy.Module):
    def __init__(self, max_context_tokens=3000):
        super().__init__()
        self.max_tokens = max_context_tokens
        self.retriever = dspy.Retrieve(k=10)
        self.ranker = dspy.ChainOfThought("question, document -> relevance_score")
        self.answer = dspy.ChainOfThought("question, context -> answer")
    
    def forward(self, question):
        # Retrieve documents
        retrieved = self.retriever(question)
        
        # Rank documents by relevance
        ranked_docs = []
        for doc in retrieved.passages:
            score = self.ranker(question=question, document=doc)
            ranked_docs.append((doc, score.relevance_score))
        
        # Sort by relevance
        ranked_docs.sort(key=lambda x: x[1], reverse=True)
        
        # Build context within token limits
        context_parts = []
        current_tokens = 0
        
        for doc, score in ranked_docs:
            # Estimate tokens (rough approximation)
            doc_tokens = len(doc.split()) * 1.3
            
            if current_tokens + doc_tokens > self.max_tokens:
                # Try to fit partial document
                remaining_tokens = self.max_tokens - current_tokens
                if remaining_tokens > 100:  # Worth including partial
                    words_to_include = int(remaining_tokens / 1.3)
                    partial_doc = ' '.join(doc.split()[:words_to_include])
                    context_parts.append(partial_doc)
                break
            
            context_parts.append(doc)
            current_tokens += doc_tokens
        
        # Generate answer with optimized context
        context = "\\n\\n".join(context_parts)
        return self.answer(question=question, context=context)`,
      testCases: [
        {
          input: 'Complex question requiring multiple documents',
          expected: 'Answer with optimal context usage',
          description: 'Should manage context efficiently',
        },
      ],
    },
    {
      id: 'dspy-m9-ex3',
      title: 'Create Production Monitoring System',
      description: 'Build a comprehensive monitoring system for DSPy applications',
      difficulty: 'expert',
      type: 'system-design',
      instructions:
        'Design and implement a monitoring system that tracks performance metrics, errors, and usage patterns for a DSPy application.',
      hints: [
        'Track request latency and success rates',
        'Monitor token usage and costs',
        'Implement alerting for anomalies',
        'Create dashboards for visualization',
      ],
      solution: `class DSPyMonitoringSystem:
    def __init__(self):
        self.metrics = {
            'requests': defaultdict(int),
            'latencies': defaultdict(list),
            'errors': defaultdict(int),
            'token_usage': defaultdict(int)
        }
        self.alerts = []
        
    def monitor_request(self, func):
        """Decorator for monitoring DSPy module calls."""
        def wrapper(*args, **kwargs):
            request_id = str(uuid.uuid4())
            start_time = time.time()
            
            try:
                # Execute function
                result = func(*args, **kwargs)
                
                # Record success metrics
                self.record_success(
                    request_id=request_id,
                    latency=time.time() - start_time,
                    module_name=func.__qualname__
                )
                
                return result
                
            except Exception as e:
                # Record error
                self.record_error(
                    request_id=request_id,
                    error=str(e),
                    module_name=func.__qualname__
                )
                raise
        
        return wrapper
    
    def record_success(self, request_id, latency, module_name):
        self.metrics['requests'][module_name] += 1
        self.metrics['latencies'][module_name].append(latency)
        
        # Check for latency anomalies
        if latency > 5.0:  # 5 second threshold
            self.trigger_alert(
                f"High latency detected: {latency:.2f}s for {module_name}"
            )
    
    def record_error(self, request_id, error, module_name):
        self.metrics['errors'][module_name] += 1
        
        # Check error rate
        total_requests = self.metrics['requests'][module_name]
        if total_requests > 100:
            error_rate = self.metrics['errors'][module_name] / total_requests
            if error_rate > 0.05:  # 5% error threshold
                self.trigger_alert(
                    f"High error rate: {error_rate:.2%} for {module_name}"
                )
    
    def get_dashboard_data(self):
        """Generate dashboard metrics."""
        dashboard = {}
        
        for module_name in self.metrics['requests']:
            latencies = self.metrics['latencies'][module_name]
            dashboard[module_name] = {
                'total_requests': self.metrics['requests'][module_name],
                'error_count': self.metrics['errors'][module_name],
                'avg_latency': sum(latencies) / len(latencies) if latencies else 0,
                'p95_latency': sorted(latencies)[int(len(latencies) * 0.95)] if latencies else 0,
                'error_rate': self.metrics['errors'][module_name] / self.metrics['requests'][module_name]
            }
        
        return dashboard

# Usage
monitor = DSPyMonitoringSystem()

class MonitoredModule(dspy.Module):
    @monitor.monitor_request
    def forward(self, input_data):
        # Your DSPy logic here
        pass`,
      testCases: [
        {
          input: 'Module execution with monitoring',
          expected: 'Metrics recorded successfully',
          description: 'Should track all relevant metrics',
        },
      ],
    },
  ],
  quiz: {
    id: 'dspy-m9-quiz',
    title: 'Module 9: Best Practices and Patterns',
    description: 'Test your understanding of DSPy best practices and design patterns',
    passingScore: 8,
    questions: [
      {
        id: 'q1',
        question: 'What is the main benefit of modular architecture in DSPy?',
        choices: [
          'It makes code longer',
          'It enables reusability, testing, and maintenance',
          'It uses more memory',
          'It makes programs slower',
        ],
        correctAnswer: 1,
        explanation:
          'Modular architecture enables code reusability, easier testing, and better maintenance by separating concerns.',
      },
      {
        id: 'q2',
        question: 'How should you handle errors in production DSPy applications?',
        choices: [
          'Let them crash the application',
          'Ignore all errors',
          'Use try-except blocks with fallback strategies',
          'Only log errors without handling',
        ],
        correctAnswer: 2,
        explanation:
          'Production applications should use try-except blocks with fallback strategies to ensure reliability.',
      },
      {
        id: 'q3',
        question: 'What is the purpose of caching in DSPy optimization?',
        choices: [
          'To use more storage',
          'To reduce API calls and improve performance',
          'To make programs slower',
          'To increase costs',
        ],
        correctAnswer: 1,
        explanation:
          'Caching reduces redundant API calls, improving performance and reducing costs.',
      },
      {
        id: 'q4',
        question: 'When should you use the Strategy pattern in DSPy?',
        choices: [
          'Never',
          'Only for simple tasks',
          'When you need different approaches for different input types',
          'Only for error handling',
        ],
        correctAnswer: 2,
        explanation:
          'The Strategy pattern is useful when you need different processing approaches based on input characteristics.',
      },
      {
        id: 'q5',
        question: 'How can you avoid context length errors in RAG systems?',
        choices: [
          'Use all available documents',
          'Ignore the context limit',
          'Rank documents and build context within limits',
          'Only use one document',
        ],
        correctAnswer: 2,
        explanation:
          'Ranking documents by relevance and building context within token limits prevents context length errors.',
      },
      {
        id: 'q6',
        question: 'What is a common pitfall when optimizing DSPy programs?',
        choices: [
          'Not optimizing at all',
          'Overfitting to small datasets',
          'Using too few examples',
          'Testing too much',
        ],
        correctAnswer: 1,
        explanation:
          'Overfitting to small datasets is a common pitfall that reduces generalization performance.',
      },
      {
        id: 'q7',
        question: 'Why is type validation important in DSPy applications?',
        choices: [
          'It makes code run faster',
          'It prevents runtime errors and ensures data consistency',
          'It uses less memory',
          'It is not important',
        ],
        correctAnswer: 1,
        explanation:
          'Type validation prevents runtime errors and ensures data consistency throughout the pipeline.',
      },
      {
        id: 'q8',
        question: 'What should a comprehensive testing strategy include?',
        choices: [
          'Only happy path tests',
          'Unit tests, integration tests, and edge case handling',
          'No tests in production',
          'Only manual testing',
        ],
        correctAnswer: 1,
        explanation:
          'Comprehensive testing includes unit tests, integration tests, and thorough edge case handling.',
      },
      {
        id: 'q9',
        question: 'How should configuration be managed in production DSPy applications?',
        choices: [
          'Hardcode all values',
          'Use environment variables and configuration files',
          'Change code for each deployment',
          'No configuration needed',
        ],
        correctAnswer: 1,
        explanation:
          'Using environment variables and configuration files enables flexible deployment across environments.',
      },
      {
        id: 'q10',
        question: 'What is the purpose of monitoring in DSPy applications?',
        choices: [
          'To slow down the application',
          'To track performance, errors, and usage patterns',
          'To increase costs',
          'Monitoring is not necessary',
        ],
        correctAnswer: 1,
        explanation:
          'Monitoring helps track performance, identify errors, and understand usage patterns for optimization.',
      },
    ],
  },
};
